import { t } from 'i18next';
import { AccountOwnerType, ExternalAccountType, MerchantBusinessCategories } from 'services/merchants/types';
import * as Yup from 'yup';

import getQueryParam from 'utils/url';

import { LABELS } from '../../keys';

export const FORM_FIELDS = {
  ACCOUNT_TYPE: 'accountType',
  BANK_NAME: 'bankName',
  BUSINESS_NAME: 'businessName',
  IBAN: 'iban',
  ACCOUNT_OWNER_NAME: 'accountOwnerName',
  ACCOUNT_OWNER_TYPE: 'accountOwnerType',
  ADDRESS: 'address',
  CATEGORY: 'category',
  SHORT_BUSINESS_DESCRIPTION: 'shortBusinessDescription',
} as const;

export const NESTED_ACCOUNT_FIELDS = {
  BIC: 'bic',
  ACCOUNT_COUNTRY: 'country',
  ACCOUNT_NUMBER: 'accountNumber',
  STREET_LINE_1: 'streetLine1',
  STREET_LINE_2: 'streetLine2',
  CITY: 'city',
  STATE: 'state',
  POSTAL_CODE: 'postalCode',
  ADDRESS_COUNTRY: 'country',
} as const;

export const KEYS = {
  ROUTING_NUMBER_MAX_LENGTH: 9,
  UNASSOCIATED_ERROR: 'unassociatedError',
  // For this payment rail we support eur only
  // Even if merchant has currency EUR we would need to lowercase it.
  // Hard coded currency brings less confusion in this case
  HARDCODED_CURRENCY: 'eur',
  HARDCODED_PURPOSE_OF_FUNDS: 'invoice_for_goods_and_services',
};

export type SepaFormField = (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS];
export type SepaFormNestedField = (typeof NESTED_ACCOUNT_FIELDS)[keyof typeof NESTED_ACCOUNT_FIELDS];

export type SepaFormValues = {
  [FORM_FIELDS.ACCOUNT_TYPE]: ExternalAccountType | undefined;
  [FORM_FIELDS.IBAN]: {
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: string;
    [NESTED_ACCOUNT_FIELDS.BIC]: string;
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY]: string;
  };
  [FORM_FIELDS.BUSINESS_NAME]: string | undefined;
  [FORM_FIELDS.BANK_NAME]: string;
  [FORM_FIELDS.ACCOUNT_OWNER_NAME]: string | undefined;
  [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: AccountOwnerType;
  [FORM_FIELDS.ADDRESS]: {
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: string;
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: string | undefined;
    [NESTED_ACCOUNT_FIELDS.CITY]: string;
    [NESTED_ACCOUNT_FIELDS.STATE]: string;
    [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: string;
    [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: string;
  };
  [FORM_FIELDS.CATEGORY]: string | undefined;
  [FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION]: string | undefined;
};

export type SepaNestedFormValues = {
  [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.BIC]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.CITY]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STATE]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: string | undefined;
};

export const categoryOptions = [
  {
    key: MerchantBusinessCategories.CLIENT,
    label: t(LABELS.CREATE_MODAL.CATEGORY.CLIENT),
  },
  {
    key: MerchantBusinessCategories.PARENT_COMPANY,
    label: t(LABELS.CREATE_MODAL.CATEGORY.PARENT_COMPANY),
  },
  {
    key: MerchantBusinessCategories.SUBSIDIARY,
    label: t(LABELS.CREATE_MODAL.CATEGORY.SUBSIDIARY),
  },
  {
    key: MerchantBusinessCategories.SUPPLIER,
    label: t(LABELS.CREATE_MODAL.CATEGORY.SUPPLIER),
  },
];

export const getValidationSchema = () =>
  Yup.object().shape({
    [FORM_FIELDS.IBAN]: Yup.object().shape({
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.BIC]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY]: Yup.string().when('$accountType', {
        is: ExternalAccountType.IBAN,
        then: (schema) => schema.required(t(LABELS.ERRORS.REQUIRED_FIELD)),
        otherwise: (schema) => schema.optional(),
      }),
    }),
    [FORM_FIELDS.BANK_NAME]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.ACCOUNT_OWNER_NAME]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.BUSINESS_NAME]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.ADDRESS]: Yup.object().shape({
      [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.CITY]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
      [NESTED_ACCOUNT_FIELDS.STATE]: Yup.string().when([NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY], {
        is: (val: any) => String(val) === 'USA',
        then: (schema) => schema.required(t(LABELS.ERRORS.REQUIRED_FIELD)),
        otherwise: (schema) => schema.optional(),
      }),
      [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    }),
    [FORM_FIELDS.CATEGORY]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
  });

const initialValues: SepaFormValues = {
  [FORM_FIELDS.ACCOUNT_TYPE]: ExternalAccountType.IBAN,
  [FORM_FIELDS.BANK_NAME]: '',
  [FORM_FIELDS.ACCOUNT_OWNER_NAME]: undefined,
  [FORM_FIELDS.IBAN]: {
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: '',
    [NESTED_ACCOUNT_FIELDS.BIC]: '',
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY]: undefined as any,
  },
  [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: AccountOwnerType.Business,
  [FORM_FIELDS.BUSINESS_NAME]: undefined,
  [FORM_FIELDS.ADDRESS]: {
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: '',
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: undefined,
    [NESTED_ACCOUNT_FIELDS.CITY]: '',
    [NESTED_ACCOUNT_FIELDS.STATE]: '',
    [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: '',
    [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: '',
  },
  [FORM_FIELDS.CATEGORY]: undefined,
  [FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION]: undefined,
};

export const getPrefilledValues = (): SepaFormValues => {
  const prefillDisabledWithQueryParam = getQueryParam('disablePrefill') === 'true';
  const prefillDisabled = window.s4cConfig.features?.canPrefillBankForm !== true;
  if (prefillDisabled || prefillDisabledWithQueryParam) {
    return initialValues;
  }

  return {
    [FORM_FIELDS.ACCOUNT_TYPE]: ExternalAccountType.IBAN,
    [FORM_FIELDS.BANK_NAME]: 'Test Bank',
    [FORM_FIELDS.ACCOUNT_OWNER_NAME]: 'John Doe',
    [FORM_FIELDS.IBAN]: {
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: '000000000123456789',
      [NESTED_ACCOUNT_FIELDS.BIC]: 'HBUKGB4B',
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_COUNTRY]: 'USA',
    },
    [FORM_FIELDS.ACCOUNT_OWNER_TYPE]: AccountOwnerType.Business,
    [FORM_FIELDS.BUSINESS_NAME]: 'Test Business',
    [FORM_FIELDS.ADDRESS]: {
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: '11208 Zena Inlet',
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: undefined,
      [NESTED_ACCOUNT_FIELDS.CITY]: 'East Milton',
      [NESTED_ACCOUNT_FIELDS.STATE]: 'NY',
      [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: '61250-4528',
      [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: 'USA',
    },
    [FORM_FIELDS.CATEGORY]: MerchantBusinessCategories.CLIENT,
    [FORM_FIELDS.SHORT_BUSINESS_DESCRIPTION]: 'Business transactions',
  };
};
